import axios from "axios";
import { useAuth } from "@clerk/react";

const baseUrl =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/";

const SIGN_IN_HASH = "#/examples/sign-in";

const apiClient = axios.create({
  baseURL: baseUrl,
  headers: { Accept: "application/json" },
});

// Clerk token injected per-request via setClerkToken (called from useApiClient hook).
let _clerkGetToken = null;
export const setClerkGetToken = (fn) => { _clerkGetToken = fn; };

apiClient.interceptors.request.use(async (config) => {
  if (_clerkGetToken) {
    const token = await _clerkGetToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if (status === 401 && !window.location.hash.includes("/examples/sign-in")) {
      window.location.hash = SIGN_IN_HASH;
    }
    return Promise.reject(error);
  },
);

/** Call this hook once near the root of the app (e.g. in HomePage) to wire Clerk tokens in. */
export function useApiClient() {
  const { getToken } = useAuth();
  setClerkGetToken(getToken);
}

export const ApiRequest = async (url, data) => {
  return apiClient.post(url, data, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const GetApiRequest = async (url, params = {}) => {
  return apiClient.get(url, { params });
};

export const PostApiRequest = async (url, data, config = {}) => {
  const defaultHeaders = {
    "Content-Type":
      data instanceof FormData ? "multipart/form-data" : "application/json",
  };
  const mergedHeaders = { ...defaultHeaders, ...(config.headers || {}) };
  return apiClient.post(url, data, { ...config, headers: mergedHeaders });
};

export const PutApiRequest = async (url, data) => {
  return apiClient.put(url, data, {
    headers: { "Content-Type": "application/json" },
  });
};

export const DeleteApiRequest = async (url) => {
  return apiClient.delete(url);
};

/**
 * Rewrites a Google Places photo reference to go through our backend proxy.
 * This keeps the API key server-side and avoids 400/expired-URL errors.
 *
 * Handles THREE input formats:
 *
 *  1. Short resource name (DB-stored from Places API v1):
 *     "places/ChIJ.../photos/ATCDNf..."
 *     → http://localhost:3000/api/viator/photo-proxy?name=places%2FChIJ...%2Fphotos%2FAUc7...&maxWidthPx=800
 *
 *  2. Full Google Places v1 URL:
 *     "https://places.googleapis.com/v1/places/ChIJ.../photos/AUc7.../media?maxWidthPx=800&key=..."
 *     → same proxy URL as above
 *
 *  3. Any other URL (S3, CDN, etc.):
 *     Returned unchanged.
 */
export function resolveImageUrl(url, maxWidthPx = "800") {
  if (url == null || url === "") return url;
  if (typeof url !== "string") {
    return typeof url === "object" && url?.url
      ? resolveImageUrl(url.url, maxWidthPx)
      : "";
  }

  // ── Format 1: short resource name stored from Places API v1 ──────────────
  // Looks like: "places/ChIJ.../photos/ATCDNf..."
  if (/^places\/[^/]+\/photos\/[^/]+/.test(url)) {
    const resourceName = url.replace(/\/media$/, "").trim();
    const imageUrl = `${baseUrl}viator/photo-proxy?name=${encodeURIComponent(resourceName)}&maxWidthPx=${maxWidthPx}`;
    return imageUrl;
  }

  // ── Format 2: full Google Places v1 media URL ─────────────────────────────
  if (url.includes("places.googleapis.com")) {
    try {
      const parsed = new URL(url);
      const resourceName = parsed.pathname
        .replace(/^\/v1\//, "")
        .replace(/\/media$/, "")
        .trim();

      const width = parsed.searchParams.get("maxWidthPx") || maxWidthPx;
      return `${baseUrl}viator/photo-proxy?name=${encodeURIComponent(resourceName)}&maxWidthPx=${width}`;
    } catch {
      return url;
    }
  }

  return url;
}

/**
 * Gray SVG as a data URL — avoids via.placeholder.com (often blocked or flaky).
 */
export function placeholderImageDataUrl(width = 60, height = 60) {
  const w = Math.max(1, Math.min(2048, Number(width) || 60));
  const h = Math.max(1, Math.min(2048, Number(height) || 60));
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><rect fill="#e5e7eb" width="100%" height="100%"/></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
